import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import { STATIC_WIZARD_PARAMS } from '../../../shared/constants';

import {
  confirmWizardParam,
  getQueryParameterState,
  hasQueryParameterResults,
  trackWizard,
  updateUrl,
} from './WizardLayout.utils';

import mq from '../../../styles/breakpoints';
import colors from '../../../styles/colors';
import elevations from '../../../styles/elevations';
import Spacings from '../../../styles/Spacings';

import { useWizardStore } from '../../../state/wizard/Wizard.store';
import { WizardOptionCondition, WizardResultState } from '../../../tokens';
import StepHeader from '../../molecules/step-header/StepHeader';
import Wizard from '../../organisms/wizard/Wizard';

import WizardContactForm from './WizardContactForm';
import WizardIntro from './WizardIntro';
import {
  parseResultConditions,
  parseResults,
  parseStoreSteps,
} from './WizardLayout.helpers';
import { WizardLayoutProps } from './WizardLayout.types';
import WizardResultLayout from './WizardResultLayout';

const WizardLayout = ({
  name,
  navigationType,
  enableReset,
  header,
  steps: rawSteps,
  intro,
  form,
  results: resultProps,
  metaData,
  pageSlug,
  parentPage,
  isIframed,
}: WizardLayoutProps) => {
  const {
    steps,
    results,
    stepIndex,
    initialize,
    reset,
    setStepIndex,
    nextStepIndex,
    previousStepIndex,
    lastStepIndex,
  } = useWizardStore();
  const [wizardInitialized, setWizardInitialized] = useState(false);
  const [showIntro, setShowIntro] = useState(!!intro);
  const [showForm, setShowForm] = useState(!!form);
  const parsedResults = parseResults(results);
  const resultConditions = parseResultConditions(results);
  const [queryParameterTags, setQueryParameterTags] = useState('');
  const renderIntro = !!intro && showIntro && stepIndex <= 0;
  const skipSteps = resultConditions.includes(WizardOptionCondition.SKIP_STEPS);
  const renderSteps = !skipSteps && stepIndex < steps.length;
  const skipForm = resultConditions.includes(WizardOptionCondition.SKIP_FORM);
  const renderForm =
    !!form &&
    showForm &&
    !skipSteps &&
    !skipForm &&
    steps.length > 0 &&
    stepIndex === steps.length;
  const renderResultsQuery = !!queryParameterTags;
  const renderResults =
    renderResultsQuery ||
    (!renderForm && steps.length > 0 && stepIndex === steps.length);
  const headerStepsProps =
    !renderIntro && !renderResults && steps.length
      ? {
          total: form ? steps.length + 1 : steps.length,
          current: stepIndex + 1,
        }
      : undefined;

  // TODO: Check if we can initialize the store default values when setting up the store.
  useEffect(() => {
    const initialSteps = parseStoreSteps(rawSteps);
    const hasQueryResults = hasQueryParameterResults();

    if (hasQueryResults) {
      const queryResults = getQueryParameterState();

      setQueryParameterTags(queryResults.tags);
    }

    initialize(initialSteps);
    setWizardInitialized(true);
  }, []);

  useEffect(() => {
    if (skipSteps) {
      lastStepIndex();
    }
  }, [results]);

  useEffect(() => {
    if (
      !wizardInitialized ||
      queryParameterTags ||
      stepIndex < 0 ||
      !steps.length ||
      (skipSteps && stepIndex < steps.length)
    )
      return;

    const resultState =
      renderResults && !results.length
        ? WizardResultState.EMPTY
        : WizardResultState.DEFAULT;

    const updatedMetaData = {
      ...metaData,
      trackingName: confirmWizardParam(STATIC_WIZARD_PARAMS.openDomain)
        ? `${metaData.trackingName} iframe`
        : metaData.trackingName,
    };

    trackWizard(
      steps,
      stepIndex,
      {
        intro: { enabled: !!intro, active: renderIntro },
        form: {
          enabled: !!form,
          active: renderForm,
          tracking: {
            name: form?.form?.trackingName || form?.form?.title || '',
          },
        },
      },
      resultConditions,
      resultState,
      updatedMetaData
    );

    updateUrl(stepIndex, {
      intro: { enabled: !!intro, active: renderIntro },
      form: { active: renderForm },
      result: {
        enabled: renderResults,
        tags: parsedResults.resultTags,
        active:
          !!results.length &&
          !resultConditions.includes(WizardOptionCondition.SKIP_STEPS),
      },
    });
  }, [stepIndex, showIntro, showForm, queryParameterTags, wizardInitialized]);

  useEffect(() => {
    window.addEventListener('popstate', handleNavigationChange);

    return () => {
      window.removeEventListener('popstate', handleNavigationChange);
    };
  }, []);

  const handleNavigationChange = () => {
    const { location } = window;
    const locationSearch = new URLSearchParams(location.search);
    const stateParameter = locationSearch.get('state');
    const indexParameter = locationSearch.get('index');

    if (!stateParameter || !indexParameter) return;

    if (stateParameter === 'intro' && intro) {
      setShowIntro(true);
    }

    if (stateParameter === 'form' && form) {
      setShowForm(true);
    }

    const parsedIndex = Number(indexParameter);

    if (Number.isInteger(parsedIndex)) {
      setStepIndex(parsedIndex);
    }
  };

  const handleOnFormError = () => {
    if (process.env.PROCESS_STAGE === 'local') {
      nextStepIndex();
    }
  };

  const handleOnPreviousStep = () => {
    if (!!intro && stepIndex === 0) {
      setShowIntro(true);
    } else {
      previousStepIndex();
    }
  };

  const handleIntroButtonClicked = () => {
    setShowIntro(false);
  };

  const handleOnFormSubmit = () => {
    setShowForm(false);
  };

  const handleOnNextStep = () => {
    nextStepIndex();
  };

  const handleOnResetSteps = () => {
    reset();
    setQueryParameterTags('');

    if (intro) {
      setShowIntro(true);
    }

    if (form) {
      setShowForm(true);
    }
  };

  return (
    <Container isEnabled={wizardInitialized} hasCardStyling={isIframed}>
      <StepHeader
        {...header}
        title={renderResults ? header.resultTitle : header.title}
        label={name}
        steps={headerStepsProps}
        enableReset={enableReset && renderResults}
        onResetSteps={handleOnResetSteps}
        iframePaddingEnabled={isIframed}
      />
      {!renderResults ? (
        <ContentWrapper hasCardStyling={isIframed}>
          {renderIntro ? (
            <WizardIntro {...intro} onButtonClick={handleIntroButtonClicked} />
          ) : renderSteps ? (
            <Wizard
              steps={steps}
              stepIndex={stepIndex}
              navigationType={navigationType}
              hidePreviousButton={!intro && stepIndex <= 0}
              isIframed={isIframed}
              onNextStep={handleOnNextStep}
              onPreviousStep={handleOnPreviousStep}
            />
          ) : renderForm ? (
            <WizardContactForm
              {...form}
              onError={handleOnFormError}
              onPreviousStep={handleOnPreviousStep}
              onFormSubmit={handleOnFormSubmit}
            />
          ) : undefined}
        </ContentWrapper>
      ) : (
        <StyledWizardResultLayout
          {...resultProps}
          results={parsedResults}
          queryTags={queryParameterTags}
          onResetButtonClick={handleOnResetSteps}
          wizardName={name}
          wizardSlug={pageSlug}
          wizardParentPage={parentPage}
          hasIframe={isIframed}
          hasCardStyling={isIframed}
        />
      )}
    </Container>
  );
};

interface ContainerProps {
  isEnabled: boolean;
  hasCardStyling: boolean;
}

const Container = styled.div<ContainerProps>`
  opacity: ${({ isEnabled }) => (isEnabled ? '1' : '0')};
  ${({ hasCardStyling }) =>
    hasCardStyling
      ? css`
          box-sizing: border-box;
          min-height: 100vh;
          padding-bottom: ${Spacings.XS};

          ${mq.from.breakpoint.L`
            max-width: 732px;
            margin: 0 auto;
          `}
        `
      : undefined}
`;

const CardBackground = css`
  background-color: ${colors.white};
  box-shadow: ${elevations.abnElevationTile};
  padding: ${Spacings.S} 0;

  ${mq.from.breakpoint.M`
    padding: ${Spacings.M} 54px;
  `}
`;

const ContentWrapper = styled.div<{
  hasCardStyling: boolean;
}>`
  margin: 0 auto;
  max-width: 736px;
  padding: ${Spacings.S};

  ${mq.from.breakpoint.M`
    padding: ${Spacings.S};
  `}

  ${mq.from.breakpoint.L`
    padding: ${Spacings.M};
  `}

  ${({ hasCardStyling }) =>
    hasCardStyling
      ? css`
          ${CardBackground};
        `
      : undefined}
`;

const StyledWizardResultLayout = styled(WizardResultLayout)<{
  hasCardStyling: boolean;
}>`
  ${({ hasCardStyling }) =>
    hasCardStyling &&
    css`
      ${CardBackground};
    `}
`;

export default WizardLayout;
